import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { openDeals, openHome } from '@/services/app/mcd.ts';
import type { CampaignUnit } from '@/services/api/types';
import CloseButton from '@/components/CloseButton';
import { pushEvent, useLoader } from '@CS/hooks';
import CSS from './Home.module.css';
import { connect } from 'react-redux';
import { M } from '@/utils/Motion';

import API from '@/services/api';
import { getLogged } from '@/redux/auth';
import { getCampaignTimezone } from '@/redux/campaign';
import Content from './content';
import { useTranslation } from '@CS/hooks';

type HomeProps = {
  timezone?: string;
  logged?: boolean;
};

type CampaignDeal = CampaignUnit & { slug?: string };

type PreparedDeal = {
  id: CampaignUnit['id'];
  title: CampaignUnit['title'];
  imageUrls: CampaignUnit['imageUrls'];
  availableDate: Date | null;
  expireDate: Date | null;
  dealSlug: string;
};

type DisplayDeal = PreparedDeal & {
  isRevealed: boolean;
  isActiveToday: boolean;
  imageSrc?: string;
};

const MOTION_IN = { opacity: 1, y: 0 };
const MOTION_OUT = { opacity: 0, y: 20 };
const MOTION_TRANSITION = { duration: 0.3, ease: 'easeOut' as const };
const SECOND_MS = 1000;
const MINUTE_MS = 60000;
const HOUR_MS = 3600000;

const preloadImage = (src?: string) =>
  new Promise<void>(resolve => {
    if (!src) {
      resolve();
      return;
    }

    const image = new Image();

    image.onload = () => resolve();
    image.onerror = () => resolve();
    image.src = src;
  });

const preloadDealImages = (items: CampaignUnit[]) =>
  Promise.all(
    items.flatMap(({ imageUrls }) => imageUrls.map(url => preloadImage(url)))
  );

const getNowInTimezone = (timezone?: string) => {
  if (!timezone) {
    return new Date();
  }

  return new Date(
    new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      hour12: false,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    }).format(new Date())
  );
};

const getValidDate = (value?: string | null) => {
  if (!value) {
    return null;
  }

  const date = new Date(value);

  return Number.isNaN(date.getTime()) ? null : date;
};

const getDealDateValue = (deal: CampaignUnit) => {
  const date = getValidDate(deal.availableAtInStore ?? deal.availableAt);

  return date?.getTime() ?? Number.MAX_SAFE_INTEGER;
};

const getNextDealTime = (preparedDeals: PreparedDeal[], now: Date) => {
  const nowTs = now.getTime();

  for (const deal of preparedDeals) {
    const { availableDate } = deal;
    if (!availableDate) {
      continue;
    }
    const availableTs = availableDate.getTime();
    if (availableTs <= nowTs) {
      continue;
    }
    const diff = availableTs - nowTs;
    const totalHours = String(Math.floor(diff / HOUR_MS)).padStart(2, '0');
    const minutes = String(Math.floor((diff % HOUR_MS) / MINUTE_MS)).padStart(
      2,
      '0'
    );
    const seconds = String(Math.floor((diff % MINUTE_MS) / SECOND_MS)).padStart(
      2,
      '0'
    );

    return `${totalHours}:${minutes}:${seconds}`;
  }

  return '00:00:00';
};

const Home = ({ timezone, logged }: HomeProps) => {
  const { title, description, cta, timer } = useTranslation(Content);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dealsRequestedRef = useRef(false);

  const [loading, setLoading] = useState(true);
  const [deals, setDeals] = useState<CampaignUnit[]>([]);
  const [animateClose, setAnimateClose] = useState(false);
  const [fullscreenImage, setFullscreenImage] = useState<string | null>(null);
  const [currentTime, setCurrentTime] = useState(() =>
    getNowInTimezone(timezone)
  );

  const loader = useLoader(loading, {
    color: '#FFBC0D'
  });

  const preparedDeals = useMemo<PreparedDeal[]>(() => {
    return [...deals]
      .sort((a, b) => getDealDateValue(a) - getDealDateValue(b))
      .map(deal => {
        const {
          id,
          title,
          imageUrls,
          availableAtInStore,
          availableAt,
          expireAtInStore,
          expireAt
        } = deal;
        const campaignDeal = deal as CampaignDeal;
        const availableDate = getValidDate(availableAtInStore ?? availableAt);
        const expireDate = getValidDate(expireAtInStore ?? expireAt);
        const dealSlug =
          campaignDeal.slug && campaignDeal.slug.trim()
            ? campaignDeal.slug
            : String(id);

        return {
          id,
          title,
          imageUrls,
          availableDate,
          expireDate,
          dealSlug
        };
      });
  }, [deals]);

  const timeLeft = useMemo(
    () => getNextDealTime(preparedDeals, currentTime),
    [preparedDeals, currentTime]
  );
  const displayDeals = useMemo<DisplayDeal[]>(() => {
    const nowTs = currentTime.getTime();
    const getStateOrder = ({ isRevealed, isActiveToday }: DisplayDeal) => {
      if (!isRevealed) {
        return 2;
      }

      return isActiveToday ? 1 : 0;
    };

    return preparedDeals
      .map(deal => {
        const { availableDate, expireDate, imageUrls } = deal;
        const availableTs = availableDate?.getTime() ?? 0;
        const expireTs = expireDate?.getTime() ?? Number.POSITIVE_INFINITY;
        const isRevealed = !!availableDate && availableTs <= nowTs;
        const isActiveToday =
          !!availableDate && availableTs <= nowTs && nowTs < expireTs;

        return {
          ...deal,
          isRevealed,
          isActiveToday,
          imageSrc: isRevealed ? imageUrls[1] : imageUrls[0]
        };
      })
      .sort((a, b) => {
        const stateDiff = getStateOrder(a) - getStateOrder(b);

        if (stateDiff !== 0) {
          return stateDiff;
        }

        return (
          (a.availableDate?.getTime() ?? 0) - (b.availableDate?.getTime() ?? 0)
        );
      });
  }, [preparedDeals, currentTime]);

  const hideGlobalClose = useCallback((hide: boolean) => {
    document.documentElement.dispatchEvent(
      new CustomEvent('toggleclose', {
        detail: !hide
      })
    );
  }, []);

  const onOpenDeals = useCallback(() => {
    pushEvent({
      eventLabel: '/ICONS26/deals/your_deals',
      eventAction: cta,
      event: 'button_click'
    });

    openDeals();
  }, [cta]);

  const onClose = () => {
    if (fullscreenImage) {
      setFullscreenImage(null);

      return;
    }

    setAnimateClose(true);

    timeoutRef.current = setTimeout(() => {
      openHome();
    }, 1000);
  };

  useEffect(() => {
    hideGlobalClose(true);

    return () => {
      hideGlobalClose(false);
    };
  }, [hideGlobalClose]);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(getNowInTimezone(timezone));
    }, SECOND_MS);

    setCurrentTime(getNowInTimezone(timezone));

    return () => {
      clearInterval(timer);
    };
  }, [timezone]);

  useEffect(() => {
    if (!logged) {
      return;
    }

    if (dealsRequestedRef.current) {
      return;
    }
    dealsRequestedRef.current = true;

    let mounted = true;

    API.getCampaignUnits()
      .then(async data => {
        if (!mounted) {
          return;
        }

        const nextDeals = Array.isArray(data) ? data : [];

        await preloadDealImages(nextDeals);

        if (!mounted) {
          return;
        }

        setDeals(nextDeals);
      })
      .catch(() => {
        if (mounted) {
          setDeals([]);
        }
      })
      .finally(() => {
        if (mounted) {
          setLoading(false);
        }
      });

    return () => {
      mounted = false;
    };
  }, [logged]);

  useEffect(() => {
    if (!fullscreenImage) {
      return;
    }

    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;

    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
    };
  }, [fullscreenImage]);

  useEffect(() => {
    pushEvent({
      event: 'page_view',
      eventProperties: {
        page_virtual_path: `/ICONS26/deals/calendar`,
        page_type: 'ICONS_2026'
      }
    });
  }, []);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    loader || (
      <section
        aria-label="Calendar"
        className={`${CSS.calendarScreen} ${animateClose ? CSS.closing : ''}`}
      >
        <CloseButton onClick={onClose} />

        <M.h2
          className={CSS.title}
          transition={{ ...MOTION_TRANSITION, delay: 0.2 }}
          animate={MOTION_IN}
          initial={MOTION_OUT}
        >
          {title}
        </M.h2>

        <M.p
          className={CSS.description}
          transition={{ ...MOTION_TRANSITION, delay: 0.4 }}
          animate={MOTION_IN}
          initial={MOTION_OUT}
        >
          {description}
        </M.p>

        <div className={CSS.cards}>
          {displayDeals.map((deal, index) => {
            const {
              id,
              title,
              imageUrls,
              dealSlug,
              isRevealed,
              isActiveToday,
              imageSrc
            } = deal;

            return (
              <M.button
                className={`${CSS.card} ${isActiveToday ? CSS.active : ''} ${isRevealed && !isActiveToday ? CSS.past : ''}`}
                animate={MOTION_IN}
                initial={MOTION_OUT}
                type="button"
                key={id}
                transition={{
                  ...MOTION_TRANSITION,
                  delay: 0.6 + index * 0.1
                }}
                onClick={() => {
                  if (!isRevealed) {
                    return;
                  }

                  pushEvent({
                    event: 'page_view',
                    eventProperties: {
                      page_virtual_path: `/ICONS26/deals/calendar/${dealSlug}`,
                      page_type: 'ICONS_2026'
                    }
                  });

                  setFullscreenImage(imageUrls[1]);
                }}
              >
                <img className={CSS.image} src={imageSrc} alt={title} />
              </M.button>
            );
          })}
        </div>

        <div className={CSS.bottomArea}>
          <M.div
            transition={{ ...MOTION_TRANSITION, delay: 1 }}
            className={CSS.footer}
            animate={MOTION_IN}
            initial={MOTION_OUT}
          >
            {timer} {timeLeft}
          </M.div>

          <M.button
            transition={{ ...MOTION_TRANSITION, delay: 1.2 }}
            className={CSS.ctaButton}
            onClick={onOpenDeals}
            animate={MOTION_IN}
            initial={MOTION_OUT}
            type="button"
          >
            {cta}
          </M.button>
        </div>

        {fullscreenImage && (
          <M.div
            className={CSS.overlay}
            transition={{ ...MOTION_TRANSITION, delay: 0 }}
            animate={{ opacity: 1 }}
            initial={{ opacity: 0 }}
            onClick={() => setFullscreenImage(null)}
          >
            <M.div
              className={CSS.modalImage}
              transition={{ ...MOTION_TRANSITION, delay: 0 }}
              animate={MOTION_IN}
              initial={MOTION_OUT}
              onClick={event => event.stopPropagation()}
            >
              <img src={fullscreenImage} alt="Fullscreen view" />
            </M.div>

            <M.button
              className={`${CSS.ctaButton} ${CSS.overlayCtaButton}`}
              transition={{ ...MOTION_TRANSITION, delay: 0.3 }}
              onClick={event => {
                event.stopPropagation();
                onOpenDeals();
              }}
              animate={MOTION_IN}
              initial={MOTION_OUT}
              type="button"
            >
              {cta}
            </M.button>
          </M.div>
        )}
      </section>
    )
  );
};

const mapStateToProps = (state: never) => ({
  logged: getLogged(state),
  timezone: getCampaignTimezone(state)
});

export default connect(mapStateToProps)(Home);
